/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@mui/material';

import { AlertDialog } from '../../../../shared/Dialog/AlertDialog';
import { assignPasswordPolicyToRealm } from '../../../../services/setting';
import { selectPasswordPolicies } from '../../../../store/passwordPolicies/passwordPoliciesSelector';
import { fetchPasswordPolicies } from '../../../../store/passwordPolicies/passwordPolicies';
import { displaySnackbar } from '../../../../store/snackBar/snackBarActionCreator';
import interpolateString from '../../../../utility/stringInterpolation';
import {
  ASSIGN_TO_REALM,
  DO_YOU_WANT_TO_ASSIGN,
  TO_THIS_REALM,
  CANCEL_BTN_TEXT,
  ASSIGN_TO_REALM_SUCCESS,
  ASSIGN_TO_REALM_ERROR,
  ASSIGN
} from './constants';
import { PasswordPolicyModel } from '../../../../models/PasswordPolicy/PasswordPolicy';

interface AssignToRealmDialogProps {
  nativeIDP: any;
  passwordPolicyId: string;
  onClose: () => void;
}

const AssignToRealmDialog = ({ nativeIDP, passwordPolicyId, onClose }: AssignToRealmDialogProps) => {
  const passwordPolicies = useSelector(selectPasswordPolicies);
  const dispatch = useDispatch();
  const [isRequesting, setIsRequesting] = useState(false);

  const passwordPolicy = passwordPolicies.find(
    (passwordPolicy: PasswordPolicyModel) => passwordPolicy.id === passwordPolicyId
  );

  const handleSubmit = async () => {
    setIsRequesting(true);
    if (nativeIDP.id) {
      try {
        await assignPasswordPolicyToRealm(nativeIDP.id, passwordPolicyId);
        fetchPasswordPolicies(dispatch);
        dispatch(displaySnackbar(interpolateString([passwordPolicy?.name], ASSIGN_TO_REALM_SUCCESS), 'success'));
      } catch (error: any) {
        dispatch(displaySnackbar(`${ASSIGN_TO_REALM_ERROR}: ${error}`, 'error'));
      }
      onClose();
    };
  };

  return (
    <AlertDialog
      dialogContentText={ASSIGN_TO_REALM}
      dialogTitle={`${DO_YOU_WANT_TO_ASSIGN} `}
      recordName={passwordPolicy?.name}
      additionalContent={<Typography component="span"> {TO_THIS_REALM}</Typography>}
      primaryButtonText={ASSIGN}
      primaryButtonColor="primary"
      secondButtonText={CANCEL_BTN_TEXT}
      handleOnCancel={onClose}
      handleOnOk={handleSubmit}
      showDialogLoader={isRequesting}
      isStatement={true}
      hideCanNotBeUndoneMessage={true}
    />
  );
};

export default AssignToRealmDialog;
